import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { BaseListLazyLoadEvent } from 'src/app/shared/components/base-list/models/base-list-event.model';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { OutboundNumberService } from '../../services/outbound-number.service';
import { OutboundNumberDto } from '../../models/outbound-number.dto';
import { OutboundNumberFormComponent } from '../outbound-number-form/outbound-number-form.component';
import { SipTransportProtocol } from '../../models/sip-transport-protocol.enum';

type OutboundTplContext = BaseListColumnTemplateContext<OutboundNumberDto>;

@Component({
  selector: 'app-outbound-numbers-list',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    TagModule,
    TooltipModule,
    ActionMenuComponent,
    BaseListComponent,
    OutboundNumberFormComponent,
  ],
  templateUrl: './outbound-numbers-list.component.html',
  styleUrl: './outbound-numbers-list.component.scss',
})
export class OutboundNumbersListComponent implements AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly outboundNumberService = inject(OutboundNumberService);
  private readonly toaster = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);

  @ViewChild('defaultTpl') defaultTpl!: TemplateRef<OutboundTplContext>;
  @ViewChild('numberTpl') numberTpl!: TemplateRef<OutboundTplContext>;
  @ViewChild('gatewayTpl') gatewayTpl!: TemplateRef<OutboundTplContext>;
  @ViewChild('protocolTpl') protocolTpl!: TemplateRef<OutboundTplContext>;
  @ViewChild('statusTpl') statusTpl!: TemplateRef<OutboundTplContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<OutboundTplContext>;

  @Output() test = new EventEmitter<OutboundNumberDto>();

  items: OutboundNumberDto[] = [];
  loading = true;
  listConfig!: BaseListConfig<OutboundNumberDto>;

  totalRecords = 0;
  skipCount = 0;
  rows = 10;

  showForm = false;
  isEditMode = false;
  selectedItem: OutboundNumberDto | null = null;

  ngAfterViewInit(): void {
    const l = (key: string): string => this.localization.instant(key);

    const columns: BaseListColumn<OutboundNumberDto>[] = [
      {
        field: 'isDefault',
        header: l('Settings::PhoneNumbers:Default'),
        width: '80px',
        bodyTemplate: this.defaultTpl,
      },
      {
        field: 'phoneNumber',
        header: l('Settings::PhoneNumbers:Number'),
        bodyTemplate: this.numberTpl,
      },
      {
        field: 'gatewayUsername',
        header: l('Settings::PhoneNumbers:Gateway'),
        bodyTemplate: this.gatewayTpl,
      },
      {
        field: 'protocol',
        header: l('Settings::PhoneNumbers:ProtocolRegister'),
        bodyTemplate: this.protocolTpl,
      },
      {
        field: 'isActive',
        header: l('Settings::PhoneNumbers:Status'),
        width: '120px',
        bodyTemplate: this.statusTpl,
      },
      {
        field: 'id',
        header: l('Settings::PhoneNumbers:Actions'),
        width: '80px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('Settings::PhoneNumbers:NoOutboundNumbersFound'),
    };

    this.cdr.detectChanges();
  }

  onLazyLoad(event: BaseListLazyLoadEvent): void {
    this.skipCount = event.skipCount;
    this.rows = event.maxResultCount;
    this.loadList();
  }

  loadList(): void {
    this.loading = true;
    this.outboundNumberService.getList(this.skipCount, this.rows).subscribe({
      next: response => {
        this.items = response.items ?? [];
        this.totalRecords = response.totalCount ?? 0;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  isActive(row: OutboundNumberDto): boolean {
    return row.isActive !== false;
  }

  getProtocolLabel(protocol?: SipTransportProtocol): string {
    if (protocol == null) {
      return '—';
    }
    return SipTransportProtocol[protocol] ?? '—';
  }

  getMenuItems(row: OutboundNumberDto): ActionMenuItem[] {
    const active = this.isActive(row);
    return [
      {
        label: this.localization.instant('Settings::PhoneNumbers:Edit'),
        icon: 'pi pi-pencil',
        action: () => this.openEdit(row),
      },
      {
        label: this.localization.instant('Settings::PhoneNumbers:TestConnectivity'),
        icon: 'pi pi-play',
        action: () => this.test.emit(row),
      },
      {
        label: this.localization.instant('Settings::PhoneNumbers:SetAsDefault'),
        icon: 'pi pi-star',
        visible: !row.isDefault,
        action: () => this.setDefault(row),
      },
      {
        label: this.localization.instant(
          active ? 'Settings::PhoneNumbers:Deactivate' : 'Settings::PhoneNumbers:Activate',
        ),
        icon: 'pi pi-power-off',
        action: () => (active ? this.deactivate(row) : this.activate(row)),
      },
      {
        label: this.localization.instant('Settings::PhoneNumbers:Remove'),
        icon: 'pi pi-trash',
        severity: 'danger',
        action: () => this.remove(row),
      },
    ];
  }

  openAdd(): void {
    this.selectedItem = null;
    this.isEditMode = false;
    this.showForm = true;
  }

  openEdit(row: OutboundNumberDto): void {
    this.selectedItem = row;
    this.isEditMode = true;
    this.showForm = true;
  }

  onFormVisibilityChange(visible: boolean): void {
    this.showForm = visible;
    if (!visible) {
      this.selectedItem = null;
    }
  }

  onFormSaved(isCreate: boolean): void {
    this.showForm = false;
    this.selectedItem = null;
    this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
    this.loadList();
  }

  setDefault(row: OutboundNumberDto): void {
    if (row.id == null || row.isDefault) {
      return;
    }
    this.outboundNumberService.setDefault(row.id).subscribe({
      next: () => {
        this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
        this.loadList();
      },
    });
  }

  private activate(row: OutboundNumberDto): void {
    if (row.id == null) {
      return;
    }
    this.outboundNumberService.activate(row.id).subscribe({
      next: () => {
        this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
        this.loadList();
      },
    });
  }

  private deactivate(row: OutboundNumberDto): void {
    if (row.id == null) {
      return;
    }
    this.outboundNumberService.deactivate(row.id).subscribe({
      next: () => {
        this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
        this.loadList();
      },
    });
  }

  private remove(row: OutboundNumberDto): void {
    if (row.id == null) {
      return;
    }
    this.confirmationService
      .warn(
        'Settings::PhoneNumbers:ConfirmRemoveOutbound',
        'Settings::PhoneNumbers:ConfirmRemoveTitle',
        {
          yesText: 'Settings::PhoneNumbers:Remove',
          cancelText: 'Settings::PhoneNumbers:Cancel',
        },
      )
      .pipe(
        switchMap(status =>
          status === Confirmation.Status.confirm
            ? this.outboundNumberService.delete(row.id!).pipe(map(() => true))
            : of(false),
        ),
      )
      .subscribe({
        next: done => {
          if (done) {
            this.toaster.success('Settings::PhoneNumbers:SuccessfullyDeleted');
            this.reloadAfterDelete();
          }
        },
      });
  }

  private reloadAfterDelete(): void {
    if (this.items.length <= 1 && this.skipCount >= this.rows) {
      this.skipCount -= this.rows;
    }
    this.loadList();
  }
}
